import React from 'react';

import PropTypes from 'prop-types';
import { FaExclamationCircle } from 'react-icons/fa';
import styled from 'styled-components';

import { Checkbox } from '@techstyle/react-components-legacy';
import { FormattedMessage } from '@techstyle/react-intl';

import { mobile } from '../../styles';
import { Component as FilterFlyout } from '../FilterFlyout';

const SIZE_REQUIRED = 'SIZE_REQUIRED';

const Container = styled.div``;

const SizeCategoryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  &:first-of-type {
    flex: 1;
  }

  flex: 0 0 100%;
  margin-bottom: ${props => props.theme.spacing.medium}px;
`;

const CupSizeWrapper = styled.ul``;
const BandSizeWrapper = styled.div``;

const BandListLabel = styled.span``;

const SelectedDot = styled.div`
  width: 4px;
  height: 4px;

  background-color: ${props => props.theme.colors.black};

  border-radius: 50%;
  position: relative;
  bottom: 6px;
  margin: 0 auto;

  ${mobile`
    bottom: 2px;
  `};
`;

const CupLabel = styled.li`
  line-height: 14px;
  text-align: center;
`;

const SizeSectionWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const SizeSelectError = styled.span`
  font-size: 12px;
  font-weight: 300;
  color: ${props => props.theme.colors.error};
  display: block;
  height: 16px;
  text-transform: none;
  letter-spacing: normal;
`;

const StyledFaExclamationCircle = styled(FaExclamationCircle)`
  margin-right: 4px;
  padding-top: 3px;
`;

const BandLabel = styled(CupLabel)`
  font-size: 10px;
  font-weight: normal;
  line-height: 10px;
`;

const BraCategoryLabel = styled.div`
  font-size: 12px;
  color: ${props => props.theme.colors.subdued};
  text-transform: uppercase;
  text-align: center;
  letter-spacing: 2px;

  ${mobile`
    font-size: 10px;
    text-align: left;
    letter-spacing: 0.83px;
    margin-top: ${props => props.theme.spacing.moderate}px;
  `};
`;

const SizeCheckbox = styled(Checkbox)`
  margin: 4px 4px 4px;
  display: inline-block;
  ${mobile`
    margin: 4px 12px 8px 4px;
  `};
  > ${Checkbox.InputWrapper} {
    border: none;
    position: relative;
    justify-content: center;
    > ${Checkbox.CustomCheckbox} {
      z-index: -1;
      width: 48px;
      height: 48px;
      border-radius: 50%;
      border: 0;
      background-color: ${props => props.theme.colors.gray150};
      margin: 0;

      > svg {
        display: none;
      }
    }

    > ${Checkbox.Label} {
      position: absolute;
      font-weight: bold;
      top: 10px;

      ${mobile`
        top: 14px;
      `};
    }
    input:checked ~ ${Checkbox.Label} {
      color: ${props => props.theme.colors.white};
    }
    input:active
      + ${Checkbox.CustomCheckbox},
      input:focus
      + ${Checkbox.CustomCheckbox} {
      border: 0;
    }
    input:checked + ${Checkbox.CustomCheckbox} {
      background-color: ${props => props.theme.colors.active};
    }
  }
`;

const CupSizeCheckbox = styled(SizeCheckbox)`
  > ${Checkbox.InputWrapper} {
    > ${Checkbox.Label} {
      ${({ hasCupLabel }) => (hasCupLabel ? 'top: 12px;' : 'top: 19px;')}

      ${mobile`${({ hasCupLabel }) =>
        hasCupLabel ? 'top: 11px;' : 'top: 19px;'}`};
    }
  }
`;

const BraCheckbox = styled(SizeCheckbox)`
  > ${Checkbox.InputWrapper} {
    > ${Checkbox.CustomCheckbox} {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      border: 0;

      > svg {
        display: none;
      }
    }
    > ${Checkbox.Label} {
      top: 2px;

      ${mobile`
        top: 6px;
      `};
    }
    input:checked ~ ${Checkbox.Label} {
      color: ${props => props.theme.colors.default};
      top: 1px;

      ${mobile`
        top: 5px;
      `};
    }
    input:checked + ${Checkbox.CustomCheckbox} {
      background-color: ${props => props.theme.colors.gray150};
      border: 2px solid ${props => props.theme.colors.active};
      padding: 0;
    }
  }
`;

const FilterLabel = styled.div.attrs(props => {
  return {
    children: (
      <>
        {props.label}
        {props.count ? ` (${props.count})` : ''}
      </>
    ),
  };
})`
  margin-right: 3px;
  margin-bottom: ${props => props.theme.spacing.small}px;
  white-space: nowrap;
  font-size: 14px;

  span {
    white-space: nowrap;
  }
`;

const StyledFilterFlyout = styled(FilterFlyout)`
  ${FilterFlyout.Popup} {
    min-width: 360px;
    max-height: 452px;
    overflow: auto;
  }
`;

class SizeFilter extends React.Component {
  static propTypes = {
    /**
     * The className to add to container.
     */
    className: PropTypes.string,
    collapsible: PropTypes.bool,
    error: PropTypes.string,
    /**
     * The filter label to render.
     */
    label: PropTypes.node,
    generateFilterCount: PropTypes.func,
    /**
     * callback with size information selected
     */
    onChooseSize: PropTypes.func,
    /**
     * callback with band size information selected
     */
    onChooseBandSize: PropTypes.func,
    /**
     * currently selected sizes
     */
    selectedSizes: PropTypes.object,
    /**
     * sizes to filter product grid by
     */
    sizes: PropTypes.array,
    /**
     * bra sizes to filter product grid by
     */
    braSizes: PropTypes.array,
    /**
     * bralette sizes to filter product grid by
     */
    braletteSizes: PropTypes.array,
    /**
     * lingerie sizes to filter product grid by
     */
    lingerieSizes: PropTypes.array,
    /**
     * men sizes to filter product grid by
     */
    menSizes: PropTypes.array,
    /**
     * undie sizes to filter product grid by
     */
    undieSizes: PropTypes.array,
  };

  static defaultProps = {
    label: 'Size',
    collapsible: true,
    generateFilterCount: () => {},
  };

  state = {
    selectedBand: '',
  };

  categorizeBraSize() {
    const { braSizes } = this.props;
    const categorizedBraSizes = [...(braSizes || [])];
    return categorizedBraSizes.map(size => {
      if (!!size.band && !!size.cup) {
        return size;
      }
      const [band, cup] = size.label.match(/[a-z]+|[^a-z]+/gi) || [];
      return {
        ...size,
        band,
        cup,
      };
    });
  }

  setBandSize = option => {
    this.setState({ selectedBand: option });
  };

  renderError = (error, field) => {
    let errorMessage = null;
    switch (error) {
      case SIZE_REQUIRED:
        if (field === 'band') {
          errorMessage = (
            <FormattedMessage
              id="site_pdp.select_band_size_error_message"
              defaultMessage="Band Selection Required"
            />
          );
        } else if (field === 'cup') {
          errorMessage = (
            <FormattedMessage
              id="site_pdp.select_cup_size_error_message"
              defaultMessage="Cup Selection Required"
            />
          );
        } else {
          errorMessage = (
            <FormattedMessage
              id="site_pdp.select_size_error_message"
              defaultMessage="Size Selection Required"
            />
          );
        }
        break;
      default:
        errorMessage = error || null;
    }
    return (
      <SizeSelectError>
        <StyledFaExclamationCircle size={13} />
        {errorMessage}
      </SizeSelectError>
    );
  };

  renderSizes(sizes, field) {
    const { selectedSizes } = this.props;
    let selectedSizesField = [];
    if (selectedSizes[field]?.length) {
      selectedSizesField = [
        ...selectedSizes.optionSignatures,
        ...selectedSizes[field],
      ];
    }

    selectedSizesField = new Set(selectedSizesField.map(size => `${size}`));

    return sizes
      ? sizes.map(size => {
          if (size.id != null) {
            const id = `${size.id}`;
            return (
              <div key={id} data-autotag="sizeFilter">
                <SizeCheckbox
                  checked={
                    selectedSizesField.size && !!selectedSizesField.has(id)
                  }
                  value={id}
                  label={size.label}
                  name={id}
                  onChange={(choiceState, _sizeId) => {
                    // If parseInt(_sizeId) returns falsey then it is a string (i.e. XS, S, M, etc)
                    // and we should return that string
                    const sizeId = parseInt(_sizeId, 10) || _sizeId;
                    this.props.onChooseSize(sizeId, field, size.id);
                  }}
                />
              </div>
            );
          }
        })
      : [];
  }

  renderBraSize() {
    const { error, selectedSizes, generateFilterCount } = this.props;
    const categorySizes = this.categorizeBraSize();
    if (!categorySizes.length) {
      return null;
    }
    const selectedBraSizes = selectedSizes.braSizes
      ? [...new Set(selectedSizes.braSizes.map(size => `${size}`))]
      : [];

    let bandSizeArray = categorySizes.map(size => {
      return size.band;
    });
    bandSizeArray = [...new Set(bandSizeArray)];

    /* If no band selected, by default we select the one from user size if it
    exist/the last selected size or we select the first band size in the list */
    let defaultBandSize = bandSizeArray[0];

    if (selectedBraSizes.length) {
      const defaultSizeToSelect = categorySizes.find(
        size => `${size.id}` === selectedBraSizes[selectedBraSizes.length - 1]
      );
      if (defaultSizeToSelect) {
        defaultBandSize = defaultSizeToSelect.band;
      }
    }

    /* Check which band size has some selection to add a "dot" in the UI under each */
    const bandSizesSelected = selectedBraSizes
      .map(selectedSize => {
        const selectedSizeInfo = categorySizes.find(
          size => `${size.id}` === selectedSize
        );
        return selectedSizeInfo ? selectedSizeInfo.band : null;
      })
      .filter(Boolean);

    const bandRadioGroup = bandSizeArray
      ? bandSizeArray.map(size => {
          const hasBandSizeSelection = !!bandSizesSelected.includes(size);
          const label = (
            <BandSizeWrapper>
              <BandListLabel>{size}</BandListLabel>
              {hasBandSizeSelection ? <SelectedDot /> : null}
            </BandSizeWrapper>
          );
          return (
            <div key={size} data-autotag="bandSizes">
              <BraCheckbox
                checked={
                  this.state.selectedBand
                    ? size === this.state.selectedBand
                    : size === defaultBandSize
                }
                value={size}
                label={label}
                name={size}
                onChange={() => {
                  this.props.onChooseBandSize &&
                    this.props.onChooseBandSize(size);
                  this.setBandSize(size);
                }}
              />
            </div>
          );
        })
      : [];

    const cupRadioGroup = categorySizes
      ? categorySizes.map(size => {
          if (size.id != null) {
            const id = `${size.id}`;

            if (
              this.state.selectedBand
                ? size.band === this.state.selectedBand
                : size.band === defaultBandSize
            ) {
              const label = (
                <CupSizeWrapper>
                  <BandLabel>{size.band}</BandLabel>
                  <CupLabel>{size.cup}</CupLabel>
                </CupSizeWrapper>
              );
              return (
                <div key={id} data-autotag="cupSizes">
                  <CupSizeCheckbox
                    checked={
                      selectedBraSizes.length && !!selectedBraSizes.includes(id)
                    }
                    value={id}
                    label={label}
                    name={id}
                    onChange={(choiceState, sizeId) => {
                      let sizeValue;
                      const hasLetters = sizeId.match('[a-zA-Z]');
                      if (hasLetters) {
                        sizeValue = sizeId;
                      } else {
                        sizeValue = parseInt(sizeId, 10);
                      }
                      return this.props.onChooseSize(
                        sizeValue,
                        'braSizes',
                        size.id
                      );
                    }}
                    hasCupLabel={!!size.cup}
                  />
                </div>
              );
            }
          }
        })
      : [];

    return (
      <SizeCategoryWrapper>
        <FilterLabel
          label={
            <FormattedMessage
              defaultMessage="Bra Size"
              id="site_product_grid.bra_size_label"
            />
          }
          count={generateFilterCount('braSizes')}
        />
        <BraCategoryLabel>
          {error && error === SIZE_REQUIRED ? (
            this.renderError(error, 'band')
          ) : (
            <FormattedMessage
              defaultMessage="Band Size"
              id="site_product_grid.band_size_label"
            />
          )}
        </BraCategoryLabel>
        <SizeSectionWrap>{bandRadioGroup}</SizeSectionWrap>
        <BraCategoryLabel>
          {error && error === SIZE_REQUIRED ? (
            this.renderError(error, 'cup')
          ) : (
            <FormattedMessage
              defaultMessage="Cup Size"
              id="site_product_grid.cup_size_label"
            />
          )}
        </BraCategoryLabel>
        <SizeSectionWrap>{cupRadioGroup}</SizeSectionWrap>
      </SizeCategoryWrapper>
    );
  }

  renderSizeFilters() {
    const {
      braletteSizes,
      lingerieSizes,
      undieSizes,
      menSizes,
      generateFilterCount,
    } = this.props;
    const braletteSizesRadio = this.renderSizes(braletteSizes, 'braletteSizes');
    const lingerieSizesRadio = this.renderSizes(lingerieSizes, 'lingerieSizes');
    const menSizesRadio = this.renderSizes(menSizes, 'menSizes');
    const undieSizesRadio = this.renderSizes(undieSizes, 'undieSizes');

    return (
      <Container>
        {this.renderBraSize()}
        {braletteSizesRadio.length ? (
          <SizeCategoryWrapper>
            <FilterLabel
              label={
                <FormattedMessage
                  defaultMessage="Bralette Size"
                  id="site_product_grid.bralette_size_label"
                />
              }
              count={generateFilterCount('braletteSizes')}
            />
            <SizeSectionWrap>{braletteSizesRadio}</SizeSectionWrap>
          </SizeCategoryWrapper>
        ) : null}
        {undieSizesRadio.length ? (
          <SizeCategoryWrapper>
            <FilterLabel
              label={
                <FormattedMessage
                  defaultMessage="Undie Size"
                  id="site_product_grid.undie_size_label"
                />
              }
              count={generateFilterCount('undieSizes')}
            />
            <SizeSectionWrap>{undieSizesRadio}</SizeSectionWrap>
          </SizeCategoryWrapper>
        ) : null}
        {lingerieSizesRadio.length ? (
          <SizeCategoryWrapper>
            <FilterLabel
              label={
                <FormattedMessage
                  defaultMessage="Lingerie Size"
                  id="site_product_grid.lingerie_size_label"
                />
              }
              count={generateFilterCount('lingerieSizes')}
            />
            <SizeSectionWrap>{lingerieSizesRadio}</SizeSectionWrap>
          </SizeCategoryWrapper>
        ) : null}
        {menSizesRadio.length &&
        !undieSizesRadio.length &&
        !lingerieSizesRadio.length &&
        !braletteSizesRadio.length ? (
          <SizeCategoryWrapper>
            <FilterLabel
              label={
                <FormattedMessage
                  defaultMessage="Men Size"
                  id="site_product_grid.men_size_label"
                />
              }
              count={generateFilterCount('menSizes')}
            />
            <SizeSectionWrap>{menSizesRadio}</SizeSectionWrap>
          </SizeCategoryWrapper>
        ) : null}
      </Container>
    );
  }

  render() {
    const { label, collapsible, className } = this.props;
    if (collapsible) {
      return (
        <StyledFilterFlyout className={className} label={label}>
          {this.renderSizeFilters()}
        </StyledFilterFlyout>
      );
    } else {
      return (
        <Container className={className}>{this.renderSizeFilters()}</Container>
      );
    }
  }
}

SizeFilter.FilterLabel = FilterLabel;
SizeFilter.StyledFilterFlyout = StyledFilterFlyout;
SizeFilter.BraCheckbox = BraCheckbox;
SizeFilter.CupSizeCheckbox = CupSizeCheckbox;
SizeFilter.SizeCheckbox = SizeCheckbox;
SizeFilter.BandLabel = BandLabel;
SizeFilter.SizeSectionWrap = SizeSectionWrap;
SizeFilter.BraCategoryLabel = BraCategoryLabel;
SizeFilter.SizeCategoryWrapper = SizeCategoryWrapper;
SizeFilter.SelectedDot = SelectedDot;

export default SizeFilter;
